import React from 'react'
import styled from '@emotion/styled'
import {Amenity} from '@findhotel/sapi/dist/types/packages/core/src/types/rooms'

import {Icon, IconName} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {Room} from '@vio/core/src/room/types/room'

import {RANKED_AMENITIES} from './constants'

type RoomAmenitiesPropsType = {
  room: Pick<Room, 'amenities'>
  onOpenRoomDetailsOverlay?: () => void
}

const Header = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${cssTheme.layout.spacing.s300};
`

const ListItem = styled.ul`
  display: grid;
  gap: ${cssTheme.layout.spacing.s300};
  margin: ${cssTheme.layout.spacing.s500} 0 0;
  padding: 0;
  list-style: none;
`

const RoomInformationItemWrapper = styled(Text)`
  display: inline-block;
`

const StyledIcon = styled(Icon)`
  display: inline-block;
  margin-right: ${cssTheme.layout.spacing.s250};
  > svg {
    vertical-align: sub;
  }
`

const RoomAmenitiesList = ({amenities}: {amenities: string[]}) => (
  <ListItem>
    {amenities.map((amenity, index) => (
      <Text key={index} as="li" variant="bodyM">
        {amenity}
      </Text>
    ))}
  </ListItem>
)

export const RoomAmenities = ({room}: RoomAmenitiesPropsType) => {
  return (
    <div>
      <Header>
        <Icon name="LightCeiling" size="md" colorPath="content.neutral.c600" />
        <Text variant="titleL">
          <FormattedMessageWrapper
            id="roomAmenities"
            defaultMessage="Room amenities"
          />
        </Text>
      </Header>
      <RoomAmenitiesList amenities={room.amenities} />
    </div>
  )
}

export const getTopRoomAmenities = (amenitiesList: Room['amenitiesList']) => {
  if (!amenitiesList || amenitiesList.length === 0) {
    return []
  }

  return RANKED_AMENITIES.reduce(
    (acc: Array<Amenity & {icon: IconName}>, rankedAmenity) => {
      if (acc.length >= 3) return acc

      const matchedAmenity = amenitiesList.find(
        amenity => amenity.id === rankedAmenity.id
      )

      if (matchedAmenity)
        return [
          ...acc,
          {
            ...matchedAmenity,
            icon: rankedAmenity.icon
          }
        ]

      return acc
    },
    []
  )
}

export const RankedTopRoomAmenities = ({
  amenitiesList
}: {
  amenitiesList: Room['amenitiesList']
}) => {
  const rankedAmenities = getTopRoomAmenities(amenitiesList)

  return (
    <>
      {rankedAmenities.map((rankedAmenity, index) => (
        <RoomInformationItemWrapper
          key={index}
          variant="bodyS"
          colorPath="content.neutral.c600"
          dataId="RoomAmenity"
        >
          <StyledIcon
            name={rankedAmenity.icon}
            size="sm"
            colorPath="content.neutral.c600"
          />
          {rankedAmenity.title}
        </RoomInformationItemWrapper>
      ))}
    </>
  )
}
